

import { useState, useEffect, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePWA } from '@/hooks/usePWA';
import { useSettings } from '@/context/SettingsContext';
import { getImageUrl } from '@/lib/utils';
import { Download, Smartphone, ArrowRight, Lock, Mail, Eye, EyeOff, Sparkles } from 'lucide-react';
import { MAIN_URL } from '@/lib/api';

const API_BASE_URL = `${MAIN_URL}/api`;

function LoginFormContent() {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ login?: string; password?: string }>({});
  const [isInstalling, setIsInstalling] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState('Shalimar ERP');
  const [tagline, setTagline] = useState('Premium Event Management');
  const [primaryColor, setPrimaryColor] = useState('#C9A84C');
  const [mounted, setMounted] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading, error, user } = useAuth();
  const { isInstallable, isStandalone, promptInstall } = usePWA();
  const { settings, isLoading: settingsLoading } = useSettings();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (settings?.logoUrl) {
      setLogoUrl(getImageUrl(settings.logoUrl));
    }
    if (settings?.business?.businessName) {
      setBusinessName(settings.business.businessName);
    }
    if (settings?.business?.tagline) {
      setTagline(settings.business.tagline);
    }
    if (settings?.theme?.primaryColor) {
      setPrimaryColor(settings.theme.primaryColor);
    }
  }, [settings]);

  useEffect(() => {
    if (!loading && user) {
      const redirect = searchParams.get('redirect');
      const isValidRedirect = (url: string | null): boolean => {
        if (!url) return false;
        if (!url.startsWith('/')) return false;
        if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) return false;
        if (url.includes('@')) return false;
        if (url.startsWith('//')) return false;
        return true;
      };
      const safeRedirect = isValidRedirect(redirect) ? redirect : null;
      navigate(safeRedirect || '/dashboard', { replace: true });
    }
  }, [user, loading, searchParams, navigate]);

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      await promptInstall();
    } catch (err) {
      console.error('Install failed:', err);
    } finally {
      setIsInstalling(false);
    }
  };

  if (loading || !mounted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
        <div className="relative">
          <div className="w-12 h-12 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const validateForm = () => {
    const newErrors: { login?: string; password?: string } = {};
    if (loginMethod === 'email') {
      if (!email) {
        newErrors.login = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        newErrors.login = 'Please enter a valid email';
      }
    } else {
      if (!phone) {
        newErrors.login = 'Phone number is required';
      } else if (phone.length < 10) {
        newErrors.login = 'Phone must be at least 10 digits';
      }
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const credentials = loginMethod === 'email' 
        ? { email, password }
        : { phone, password };
      await login(credentials);
      const redirect = searchParams.get('redirect');
      const isValidRedirect = (url: string | null): boolean => {
        if (!url) return false;
        if (!url.startsWith('/')) return false;
        if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) return false;
        if (url.includes('@')) return false;
        if (url.startsWith('//')) return false;
        return true;
      };
      const safeRedirect = isValidRedirect(redirect) ? redirect : '/dashboard';
      navigate(safeRedirect || '/dashboard', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-gradient-to-br from-white via-stone-50 to-amber-50">
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950 via-stone-900 to-amber-950" />
        <div className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full bg-amber-500/15 blur-3xl transform translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full bg-amber-400/15 blur-3xl transform -translate-x-1/3 translate-y-1/3" />
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(rgba(201,168,76,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
        <div className="relative z-10 flex flex-col justify-center items-center w-full px-12">
          <div className="max-w-lg text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 flex items-center justify-center">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt={businessName}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <Sparkles className="w-12 h-12 text-amber-400" />
                )}
              </div>
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white mb-4 tracking-tight">
              {businessName}
            </h1>
            <p className="text-xl text-stone-300 mb-8">
              {tagline}
            </p>
            <div className="grid grid-cols-2 gap-4 text-left mt-12">
              {[
                { label: 'Event Management', desc: 'Plan & execute events' },
                { label: 'Vendor Coordination', desc: 'Manage suppliers' },
                { label: 'Real-time Tracking', desc: 'Monitor progress' },
                { label: 'Financial Reports', desc: 'Track expenses' },
              ].map((feature, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <div className="w-2 h-2 rounded-full bg-amber-400 mb-2" />
                  <p className="text-white font-medium text-sm">{feature.label}</p>
                  <p className="text-stone-400 text-xs">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col justify-center items-center p-6 sm:p-8 lg:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-200 p-3 flex items-center justify-center mb-4">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={businessName}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).parentElement!.innerHTML = '<span class="text-2xl font-bold text-amber-600">S</span>';
                  }}
                />
              ) : (
                <span className="text-2xl font-bold text-amber-600">S</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-stone-800">{businessName}</h1>
            <p className="text-sm text-stone-500">{tagline}</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-stone-800 mb-2">Welcome back</h2>
            <p className="text-stone-500 text-sm">Please enter your details to sign in</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-1 p-1 bg-stone-100 rounded-xl mb-6">
            <button
              type="button"
              onClick={() => setLoginMethod('email')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${
                loginMethod === 'email'
                  ? 'bg-white text-stone-800 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => setLoginMethod('phone')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${
                loginMethod === 'phone'
                  ? 'bg-white text-stone-800 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Phone
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {loginMethod === 'email' ? 'Email Address' : 'Phone Number'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stone-400">
                  {loginMethod === 'email' ? <Mail className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
                </div>
                {loginMethod === 'email' ? (
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full bg-stone-50 border ${errors.login ? 'border-red-300' : 'border-stone-200'} rounded-xl py-3 pl-12 pr-4 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all`}
                    placeholder="you@company.com"
                  />
                ) : (
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    className={`w-full bg-stone-50 border ${errors.login ? 'border-red-300' : 'border-stone-200'} rounded-xl py-3 pl-12 pr-4 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all`}
                    placeholder="9876543210"
                    maxLength={15}
                  />
                )}
              </div>
              {errors.login && <p className="text-xs text-red-600 mt-1">{errors.login}</p>}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-sm font-medium text-stone-700">Password</label>
                <a href="#" className="text-xs text-amber-600 hover:text-amber-700">Forgot password?</a>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stone-400">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full bg-stone-50 border ${errors.password ? 'border-red-300' : 'border-stone-200'} rounded-xl py-3 pl-12 pr-12 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-stone-900 hover:bg-stone-800 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {isInstallable && !isStandalone && (
            <button
              type="button"
              onClick={handleInstall}
              disabled={isInstalling}
              className="w-full mt-6 py-3 px-4 bg-white hover:bg-stone-50 border border-stone-200 rounded-xl text-stone-700 text-sm font-medium transition-all flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4 text-amber-600" />
              {isInstalling ? 'Installing...' : 'Install App'}
            </button>
          )}

          <p className="text-center text-stone-400 text-xs mt-8">
            &copy; {new Date().getFullYear()} {businessName}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <LoginFormContent />
    </Suspense>
  );
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      <div className="relative w-20 h-20">
        <div className="absolute inset-0 border-4 border-t-amber-500 border-r-amber-500 border-b-transparent border-l-transparent rounded-full animate-spin"></div>
        <div className="absolute inset-2 border-4 border-t-transparent border-r-transparent border-b-amber-300 border-l-amber-300 rounded-full animate-spin-reverse"></div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <LoginForm />
    </Suspense>
  );
}
